import { fromJS, List, Map, Set } from 'immutable';
import getIn from 'lodash/get';
import { REHYDRATE } from 'redux-persist/constants';

import * as StationConstants from './constants';
import * as D from './deps';

const INITIAL_STATE = Map({
  [D.Constants.Station.StationConstants.STATIONS_ERROR_KEY]: '',
  [D.Constants.Station.StationConstants.STATIONS_KEY]: List([]),
  /*
  Used when start/switching stations. The next station to play after finishing the bussiness logic in usescases/startStation
  e.g. T1 users should see a video ad before starting a station.
  The station that will play after the video ad will be the next station.
  */
  [D.Constants.Station.StationConstants.NEXT_STATION]: '',
  [D.Constants.Station.StationConstants.STATION_HISTORY_KEY]: List([]),
  [D.Constants.Station.StationConstants.CURRENT_STATION]: '',
  [D.Constants.Station.StationConstants.TOTAL_STATION_COUNT]: 0,
  [D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED]: null,
  [D.Constants.Station.StationConstants.SHUFFLE_STATION]: '',
  [D.Constants.Station.StationConstants.STATIONS_THAT_HAVE_RECEIVED_FRAGMENTS]: Set([]),
  [D.Constants.Station.StationConstants.IS_IN_SHUFFLE_MODE]: false,
  [D.Constants.Station.StationConstants.IS_IN_SHUFFLE_ALL_MODE]: false,
  [D.Constants.Station.StationConstants.IS_IN_EDIT_MODE]: false,
  [D.Constants.Station.StationConstants.RECOMMENDATION_RESULTS]: Map(),
  [D.Constants.Station.StationConstants.SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT]: false,
  [StationConstants.keys.INTERACTIVE_RADIO_MODE]: '',
  [StationConstants.keys.INTERACTIVE_RADIO_AVAILABLE_MODES]: List(),
  [StationConstants.keys.HAS_TAKEOVER_MODES]: false,
});

// STATION REDUCER
export default D.createReducer(INITIAL_STATE, {
  [REHYDRATE]: onRehydrate,
  [D.Constants.Api.ApiActions.FETCH_STATIONS_SUCCESS]: fetchStationsSuccess,
  [D.Constants.Playback.ActionTypes.ADD_TRACK_TO_STATION_HISTORY]: addTrackToStationHistory,
  [D.Constants.Api.ApiActions.CREATE_STATION_SUCCESS]: createStationSuccess,
  [D.Constants.Playback.ActionTypes.REMOVE_STATION]: removeStation,
  [D.Constants.Playback.ActionTypes.UNDO_REMOVE_STATION]: undoRemoveStation,
  [D.Constants.Playback.ActionTypes.SET_STATION_TO_BE_REMOVED_FROM_SERVER]:
    setStationToBeRemovedFromServer,
  [D.Constants.Playback.ActionTypes.CLEAR_STATION_TO_BE_REMOVED_FROM_SERVER]:
    clearStationToBeRemovedFromServer,
  [D.Constants.Playback.ActionTypes.SET_OR_CREATE_STATION_FINISHED]: stationSelected,
  [D.Constants.Api.ApiActions.GET_FRAGMENT_SUCCESS]: getFragmentSuccess,
  [D.Constants.Api.ApiActions.PLAYBACK_SOURCE_SUCCESS]: getSourceSuccess,
  [D.Constants.Api.ApiActions.GET_OR_SET_SHUFFLE_STATIONS_SUCCESS]: getOrSetStationShuffle,
  [D.Constants.Playback.ActionTypes.SET_IS_IN_STATION_SHUFFLE_MODE]: setIsInStationShuffleMode,
  [D.Constants.Playback.ActionTypes.TOGGLE_SHUFFLE_ALL_STATIONS_MODE]: toggleShuffleAllMode,
  [D.Constants.Playback.ActionTypes.SET_STATION_RECEIVED_FRAGMENT]: setStationReceivedFragment,
  [D.Constants.Api.ApiActions.LOGOUT_SUCCESS]: onLogoutSuccess,
  [D.Constants.Api.ApiActions.LOGIN_SUCCESS]: onAuthSuccess,
  [D.Constants.Api.ApiActions.DEVICE_LOGIN_SUCCESS]: onAuthSuccess,
  [D.Constants.Api.ApiActions.ANON_LOGIN_SUCCESS]: onAuthSuccess,
  [D.Constants.Api.ApiActions.ANON_LOGIN_ATTEMPT]: resetCurrentStation,
  [D.Constants.Api.ApiActions.REGISTER_SUCCESS]: onAuthSuccess,
  [D.Constants.Api.ApiActions.SEARCH_RECOMMENDATIONS_SUCCESS]: searchRecommendationsSuccess,
  [D.Constants.Playback.ActionTypes.SET_PLAYLIST]: onSetPlaylist,
  [D.Constants.Playback.ActionTypes.SET_OR_CREATE_STATION]: onSetOrCreateStation,
  [D.Constants.Api.ApiActions.INTERACTIVE_RADIO_SET_MODE_ATTEMPT]: onSetInteractiveRadioModeAttempt,
  [D.Constants.Api.ApiActions.INTERACTIVE_RADIO_SET_MODE_SUCCESS]: onInteractiveRadioModesSuccess,
  [D.Constants.Api.ApiActions.INTERACTIVE_RADIO_SET_MODE_FAIL]: onSetInteractiveRadioModeFail,
  [D.Constants.Api.ApiActions.INTERACTIVE_RADIO_GET_MODES_SUCCESS]: onInteractiveRadioModesSuccess,
  [StationConstants.actions.SET_SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT]:
    setShouldShortCircuitAfterGetFragment,
  [StationConstants.actions.TOGGLE_EDIT_MODE]: toggleEditMode,
});

// REDUCER UTILITY

/*
 * Internal utility for checking whether a track is in track history
 */

function isTrackInHistory(state, trackToken) {
  if (D.Utils.Music.isOnDemandIdLike(trackToken)) {
    return false;
  }
  return !!state
    .get(D.Constants.Station.StationConstants.STATION_HISTORY_KEY)
    .find(historyEntry => {
      return historyEntry.get('trackToken') === trackToken;
    });
}

// CASE FUNCTIONS

function onRehydrate(state, action) {
  return state.mergeDeep(action.payload.stations);
}

function fetchStationsSuccess(state, action) {
  D.Utils.Time.markTimeAtMoment(D.Constants.App.ApiCheckpointMarks.FETCH_STATIONS_RESPONSE);
  // throw away old station list, replace it with new one
  // for now we assume that we're only going to call once to get the MAX number of stations
  const stationIds = action.response.result.stations;
  return state.merge({
    [D.Constants.Station.StationConstants.STATIONS_ERROR_KEY]: '',
    [D.Constants.Station.StationConstants.TOTAL_STATION_COUNT]:
      action.response.result.totalStations,
    [D.Constants.Station.StationConstants.NEXT_STATION]: stationIds && stationIds[0],
    [D.Constants.Station.StationConstants.STATIONS_KEY]: List(stationIds),
  });
}

function addTrackToStationHistory(state, action) {
  const actionStationId = action.data.stationId;
  const nextTrack = action.data.track;
  const isNewTrackInHistory = isTrackInHistory(state, nextTrack.get('trackToken'));

  if (actionStationId && nextTrack && !isNewTrackInHistory) {
    let stationTrackHistory = state.getIn(
      [D.Constants.Station.StationConstants.STATION_HISTORY_KEY],
      List(),
    );
    const nextTrackToken = D.Utils.Core.getFromMap(nextTrack, 'trackToken');
    const nextTrackType = nextTrack.get('trackType', nextTrack.get('type'));
    const previousHistoryItem = stationTrackHistory.last();

    if (
      previousHistoryItem &&
      !D.Utils.Playlist.shouldTypePersistInSessionHistory(previousHistoryItem.get('trackType'))
    ) {
      stationTrackHistory = stationTrackHistory.pop();
    }
    stationTrackHistory = stationTrackHistory.push(
      new Map({
        trackToken: nextTrackToken,
        stationId: actionStationId,
        trackType: nextTrackType,
      }),
    );
    stationTrackHistory = stationTrackHistory.slice(-D.Constants.Playback.SESSION_HISTORY_LIMIT);

    return state.setIn(
      [D.Constants.Station.StationConstants.STATION_HISTORY_KEY],
      stationTrackHistory,
    );
  }

  return state;
}

function createStationSuccess(state, action) {
  // add the new station to the stations, if not already there
  const stationId = action.response.result;
  let stations = state.getIn([D.Constants.Station.StationConstants.STATIONS_KEY]);

  if (!stations.includes(stationId)) {
    stations = stations.push(stationId);

    return state.withMutations(substate => {
      substate
        .set(D.Constants.Station.StationConstants.STATIONS_ERROR_KEY, '')
        .update(
          D.Constants.Station.StationConstants.TOTAL_STATION_COUNT,
          totalStationCount => totalStationCount + 1,
        )
        .set(D.Constants.Station.StationConstants.NEXT_STATION, stationId)
        .set(D.Constants.Station.StationConstants.STATIONS_KEY, List(stations));
    });
  }

  return state;
}

function removeStation(state, action) {
  const removedId = action.data.stationId;
  const stations = state
    .getIn([D.Constants.Station.StationConstants.STATIONS_KEY])
    .filter(currId => currId !== removedId);

  return state.withMutations(substate => {
    substate
      .update(
        D.Constants.Station.StationConstants.TOTAL_STATION_COUNT,
        totalStationCount => totalStationCount - 1,
      )
      .set(D.Constants.Station.StationConstants.STATIONS_KEY, List(stations));
  });
}

function undoRemoveStation(state, action) {
  const restoredId = action.data.stationId;
  let stations = state.getIn([D.Constants.Station.StationConstants.STATIONS_KEY], List());

  if (!stations.includes(restoredId)) {
    stations = stations.push(restoredId);

    return state.withMutations(substate => {
      substate
        .set(
          D.Constants.Station.StationConstants.STATIONS_ERROR_KEY,
          INITIAL_STATE.get(D.Constants.Station.StationConstants.STATIONS_ERROR_KEY),
        )
        .set(
          D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED,
          INITIAL_STATE.get(D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED),
        )
        .update(
          D.Constants.Station.StationConstants.TOTAL_STATION_COUNT,
          totalStationCount => totalStationCount + 1,
        )
        .set(D.Constants.Station.StationConstants.STATIONS_KEY, List(stations));
    });
  }
  return state;
}

function setStationToBeRemovedFromServer(state, action) {
  return state.set(
    D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED,
    action.data.stationId,
  );
}

function clearStationToBeRemovedFromServer(state) {
  return state.set(
    D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED,
    INITIAL_STATE.get(D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED),
  );
}

function stationSelected(state, { data }) {
  return state.merge({
    [D.Constants.Station.StationConstants.CURRENT_STATION]: data.stationId,
    [D.Constants.Station.StationConstants.NEXT_STATION]: data.stationId,
  });
}

function getFragmentSuccess(state, action) {
  // This is for the rare case when a reauth blows away the current station (via LOGIN_SUCCESS)
  if (!state.get(D.Constants.Station.StationConstants.CURRENT_STATION)) {
    if (action && action.updateEntitiesWith && action.updateEntitiesWith.stationId) {
      return state.set(
        D.Constants.Station.StationConstants.CURRENT_STATION,
        action.updateEntitiesWith.stationId,
      );
    }
  }
  return state;
}

function getSourceSuccess(state, action) {
  const stationId = action.response.source.pandoraId;
  if (stationId) {
    return state.set(
      D.Constants.Station.StationConstants.CURRENT_STATION,
      action.response.source.pandoraId.split(':').pop(),
    );
  }
  return state;
}

function getOrSetStationShuffle(state, action) {
  const shuffleId = action.response.result;
  return state.set(D.Constants.Station.StationConstants.SHUFFLE_STATION, shuffleId);
}

function setIsInStationShuffleMode(state, action) {
  return state.set(
    D.Constants.Station.StationConstants.IS_IN_SHUFFLE_MODE,
    !!action.data.toShuffle,
  );
}

function toggleShuffleAllMode(state, action) {
  return state.set(
    D.Constants.Station.StationConstants.IS_IN_SHUFFLE_ALL_MODE,
    !!action.data.shuffleAll,
  );
}

function setStationReceivedFragment(state, action) {
  const stationId = action.data.stationId;

  return state.update('stationsThatHaveReceivedFragments', value => {
    return value.union([stationId]);
  });
}

function resetCurrentStation(state) {
  return state.set(D.Constants.Station.StationConstants.CURRENT_STATION, '');
}

function onAuthSuccess(state, action) {
  if (action.isUserInitiated || action.isCustomerServiceLogin) {
    return INITIAL_STATE;
  }
  return state;
}

function onLogoutSuccess() {
  return INITIAL_STATE;
}

function searchRecommendationsSuccess(state, action) {
  const result = (action && action.response && action.response.result) || [];
  return state.set(D.Constants.Station.StationConstants.RECOMMENDATION_RESULTS, fromJS(result));
}

function onSetPlaylist(state, action) {
  if (action.data.playlistType !== D.Constants.SourceTypes.SourceTypes.STATION) {
    return state.set(D.Constants.Station.StationConstants.CURRENT_STATION, '');
  }
  return state;
}

function onSetInteractiveRadioModeAttempt(state, action) {
  const modeId = getIn(action, 'request.content.modeId');
  return state.set(StationConstants.keys.INTERACTIVE_RADIO_MODE, modeId);
}

function onSetInteractiveRadioModeFail(state, action) {
  return state.set(
    StationConstants.keys.INTERACTIVE_RADIO_MODE,
    getIn(action, 'request.content.previousModeId'),
  );
}

function onInteractiveRadioModesSuccess(state, action) {
  return state.merge({
    [StationConstants.keys.INTERACTIVE_RADIO_MODE]: Number.isInteger(action.response.currentModeId)
      ? action.response.currentModeId
      : INITIAL_STATE.get(StationConstants.keys.INTERACTIVE_RADIO_MODE),
    [StationConstants.keys.INTERACTIVE_RADIO_AVAILABLE_MODES]:
      action.response.availableModes ||
      INITIAL_STATE.get(StationConstants.keys.INTERACTIVE_RADIO_AVAILABLE_MODES),
    [StationConstants.keys.HAS_TAKEOVER_MODES]: action.response.hasTakeoverModes,
  });
}

function setShouldShortCircuitAfterGetFragment(state, action) {
  return state.set(
    D.Constants.Station.StationConstants.SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT,
    action.data.shouldShortCircuitAfterGetFragment,
  );
}

function onSetOrCreateStation(state, { data }) {
  return state.set(D.Constants.Station.StationConstants.NEXT_STATION, data.stationId);
}

function toggleEditMode(state, action) {
  const { editMode } = action.data;
  return state.set(D.Constants.Station.StationConstants.IS_IN_EDIT_MODE, editMode);
}
